import { useTranslation } from 'react-i18next';

import { useSetMediaTags, type BrandMediaItem } from '@/entities/brand-media';
import { notificationService } from '@/lib/services/notification.service';

type SetTags = ReturnType<typeof useSetMediaTags>;

/**
 * Writes one key on each of a set of assets, and says once how it went.
 *
 * One request per asset, each carrying that asset's own values for the key, because the endpoint
 * replaces a key rather than appending to it: sending the same list to every row would flatten
 * whatever they already had.
 *
 * Fired together rather than through a queue. The set is one page of the grid, so the ceiling is
 * the listing's page size rather than the size of the library, and the browser holds its own cap
 * on requests in flight to one origin underneath that. A pool here would be a second limit on top
 * of those two, tuned against nothing. If the grid ever pages or scrolls endlessly, this stops
 * being bounded by a page and wants revisiting with it.
 *
 * Shared by the two directions because the batch is the same either way. What differs is only which
 * rows need touching and what each one's new list is, which is what the callers below supply.
 *
 * One notice for the batch rather than one per asset, and it carries the split when the batch was
 * a split: "some of them" is the thing a person has to be told, since the rest did go through.
 * Which ones failed is answered by repeating the gesture rather than by naming them -- both
 * callers below filter to the rows that still need the write, so a second press retries exactly
 * the failures and nothing else. That only holds while the selection survives, so neither caller
 * clears it.
 */
const commitBatch = async (
  setTags: SetTags,
  rows: readonly BrandMediaItem[],
  key: string,
  nextValues: (row: BrandMediaItem) => string[],
  copy: {
    failed: string;
    partial: (done: number, failed: number) => string;
    done: (count: number) => string;
  },
) => {
  const results = await Promise.allSettled(
    rows.map((row) => setTags.mutateAsync({ mediaId: row.id, tags: { [key]: nextValues(row) } })),
  );

  const failed = results.filter((result) => result.status === 'rejected').length;

  if (failed === rows.length) {
    notificationService.showError(copy.failed);
  } else if (failed > 0) {
    notificationService.showError(copy.partial(rows.length - failed, failed));
  } else {
    notificationService.showSuccess(copy.done(rows.length));
  }
};

/**
 * Puts one tag on a set of assets.
 *
 * Assets already carrying the pair are left out rather than rewritten, so the number reported back
 * is what actually changed, and applying a tag over a selection that partly has it already costs
 * only the rows that needed it.
 */
export const useApplyTag = () => {
  const { t } = useTranslation('tools');
  const setTags = useSetMediaTags();

  const apply = async (rows: readonly BrandMediaItem[], key: string, value: string) => {
    const missing = rows.filter((row) => !(row.tags[key] ?? []).includes(value));

    if (missing.length === 0) {
      notificationService.showInfo(t('brandMedia.tagAlreadyThere', { count: rows.length }));
      return;
    }

    await commitBatch(setTags, missing, key, (row) => [...(row.tags[key] ?? []), value], {
      failed: t('brandMedia.tagSelectedFailed'),
      partial: (done, count) => t('brandMedia.tagSelectedPartial', { done, count }),
      done: (count) => t('brandMedia.tagSelectedDone', { count }),
    });
  };

  return { apply, isPending: setTags.isPending };
};

/**
 * Takes one tag off a set of assets, and only off the ones carrying it.
 *
 * The mirror of `useApplyTag`, and deliberately not a library-wide removal: this acts on the rows
 * somebody ticked, which is a different gesture from retiring the tag itself. The endpoint behind
 * that one is `useRemoveTagValue`, reached from the tag's own menu.
 *
 * A key emptied of its last value is sent as an empty array, which the server reads as a removal
 * rather than storing `[]` -- the same contract the per-asset editor's own delete relies on.
 */
export const useRemoveTag = () => {
  const { t } = useTranslation('tools');
  const setTags = useSetMediaTags();

  const remove = async (rows: readonly BrandMediaItem[], key: string, value: string) => {
    const carrying = rows.filter((row) => (row.tags[key] ?? []).includes(value));

    if (carrying.length === 0) {
      notificationService.showInfo(t('brandMedia.tagNotThere', { count: rows.length }));
      return;
    }

    await commitBatch(
      setTags,
      carrying,
      key,
      (row) => (row.tags[key] ?? []).filter((held) => held !== value),
      {
        failed: t('brandMedia.untagSelectedFailed'),
        partial: (done, count) => t('brandMedia.untagSelectedPartial', { done, count }),
        done: (count) => t('brandMedia.untagSelectedDone', { count }),
      },
    );
  };

  return { remove, isPending: setTags.isPending };
};

/** Whether every row in a non-empty selection already carries the pair, which is what flips the row's control. */
export const allCarryTag = (rows: readonly BrandMediaItem[], key: string, value: string): boolean =>
  rows.length > 0 && rows.every((row) => (row.tags[key] ?? []).includes(value));
